import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Skeleton, Alert, Chip, Stack } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { sub } from 'date-fns';
import { dashboardAPI } from '../../services/dashboard-api';
import { formatCurrency, formatCurrencyCompact } from '../../utils/formatters';

const CHART_HEIGHT = 300;
// Width reserved for the Y-axis band (must fit the widest tick label, e.g. "€-1.2k").
const Y_AXIS_WIDTH = 52;

const fmtCurrencyFull = (v) => (v == null ? '' : formatCurrency(v, { maximumFractionDigits: 0 }));

const PRESETS = [
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '6m', days: 183 },
  { label: '12m', days: 365 },
];

// Legend rendered below the chart, aligned to the plot area (past the Y-axis).
const Legend = ({ series }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={{ xs: 0.5, sm: 2 }}
    sx={{ mt: 1, pl: `${Y_AXIS_WIDTH}px`, flexWrap: 'wrap' }}
  >
    {series.map((s) => (
      <Stack key={s.label} direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: s.color, flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary">{s.label}</Typography>
      </Stack>
    ))}
  </Stack>
);

// A single chart that fills its container width — no horizontal scroll, so the
// Y-axis is always visible and there's no gutter/gap before the bars. The
// backend coarsens buckets for long ranges (day→week→month), so the bar count
// stays readable at every preset instead of overflowing.
const FitChart = ({ ChartComponent, xAxis, series, yValueFormatter, yDomain, extraProps = {} }) => {
  const yAxis = {
    valueFormatter: yValueFormatter,
    width: Y_AXIS_WIDTH,
    ...(yDomain?.min != null ? { min: yDomain.min } : {}),
    ...(yDomain?.max != null ? { max: yDomain.max } : {}),
  };
  return (
    // Omitting `width` makes MUI size the chart to this Box's width (responsive).
    <Box sx={{ width: '100%' }}>
      <ChartComponent
        height={CHART_HEIGHT}
        margin={{ top: 10, bottom: 30, left: 0, right: 12 }}
        xAxis={[xAxis]}
        yAxis={[yAxis]}
        series={series}
        hideLegend
        {...extraProps}
      />
    </Box>
  );
};

function rollingConvRate(data, clicksKey, ordersKey) {
  return data.map((_, i) => {
    const window = data.slice(Math.max(0, i - 6), i + 1);
    const clicks = window.reduce((s, d) => s + (d[clicksKey] || 0), 0);
    const orders = window.reduce((s, d) => s + (d[ordersKey] || 0), 0);
    if (clicks < 5) return null;
    return Math.round((orders / clicks) * 10000) / 100;
  });
}

// Nice domain covering every value (incl. negatives) with a little headroom, so
// no bar is clipped and the top/bottom ticks render with room to spare.
function niceDomain(values) {
  const nums = values.filter((v) => v != null && !Number.isNaN(v));
  if (nums.length === 0) return { min: 0, max: 1 };
  let min = Math.min(0, ...nums);
  let max = Math.max(0, ...nums);
  if (min === 0 && max === 0) return { min: 0, max: 1 };
  const pad = (max - min) * 0.08;
  max += pad;
  if (min < 0) min -= pad;
  return { min, max };
}

const TrendCharts = ({ myRetailerId }) => {
  const [preset, setPreset] = useState('90d');
  const [trends, setTrends] = useState([]);
  const [granularity, setGranularity] = useState('week');
  const [conversionTrends, setConversionTrends] = useState([]);
  const [cohortRate, setCohortRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [convLoading, setConvLoading] = useState(false);
  const [error, setError] = useState('');
  const [chartView, setChartView] = useState('revenue');

  const { startDate, endDate } = useMemo(() => {
    const days = PRESETS.find((p) => p.label === preset)?.days || 90;
    return { startDate: sub(new Date(), { days }), endDate: new Date() };
  }, [preset]);

  const loadTrends = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await dashboardAPI.getTrends({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        my_retailer_id: myRetailerId || undefined,
      });
      if (response.success) {
        setTrends(response.data || []);
        setGranularity(response.granularity || 'week');
        setCohortRate(response.cohort_inbound_conversion_rate ?? null);
      } else {
        setError('Failed to load trend data');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, myRetailerId]);

  const loadConversionTrends = useCallback(async () => {
    try {
      setConvLoading(true);
      const response = await dashboardAPI.getTrends({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        my_retailer_id: myRetailerId || undefined,
        granularity: 'day',
      });
      if (response.success) {
        setConversionTrends(response.data || []);
        if (response.cohort_inbound_conversion_rate != null) {
          setCohortRate(response.cohort_inbound_conversion_rate);
        }
      }
    } catch {
      // Non-critical — conversion chart will just be empty
    } finally {
      setConvLoading(false);
    }
  }, [startDate, endDate, myRetailerId]);

  useEffect(() => {
    loadTrends();
  }, [loadTrends]);

  useEffect(() => {
    if (chartView === 'conversion') {
      loadConversionTrends();
    }
  }, [chartView, loadConversionTrends]);

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>Trends</Typography>
        <Skeleton variant="rectangular" height={CHART_HEIGHT} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  const xLabels = trends.map((t) => {
    const d = new Date(t.period);
    if (granularity === 'day') {
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    if (granularity === 'week') {
      const end = new Date(d);
      end.setDate(end.getDate() + 6);
      const startLabel = d.toLocaleDateString(undefined, { day: 'numeric' });
      const endLabel = end.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
      return `${startLabel}–${endLabel}`;
    }
    return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  });

  const convXLabels = conversionTrends.map((t) => {
    const d = new Date(t.period);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  });

  const barXAxis = { data: xLabels, scaleType: 'band' };

  const rollingRecv = rollingConvRate(conversionTrends, 'clicks_received', 'orders_acquired');
  const rollingSent = rollingConvRate(conversionTrends, 'clicks_sent', 'orders_sold');
  const flatCohort = cohortRate != null ? conversionTrends.map(() => cohortRate) : [];

  // Series definitions per view (also drive the custom legend).
  const revenueSeries = [
    { data: trends.map((t) => t.revenue_generated), label: 'Revenue generated', color: '#4caf50', valueFormatter: fmtCurrencyFull },
    { data: trends.map((t) => t.net_acquisition_cost), label: 'Net acquisition cost', color: '#ff9800', valueFormatter: fmtCurrencyFull },
  ];
  const ordersSeries = [
    { data: trends.map((t) => t.orders_bought || t.orders_acquired || 0), label: 'Orders received', color: '#1976d2' },
    { data: trends.map((t) => t.orders_sold || 0), label: 'Orders sent', color: '#90caf9' },
  ];
  const spendSeries = [
    { data: trends.map((t) => t.acquisition_spend), label: 'Acquisition spend', color: '#ff9800', valueFormatter: fmtCurrencyFull },
    { data: trends.map((t) => t.affiliate_revenue_earned), label: 'Affiliate revenue earned', color: '#4caf50', valueFormatter: fmtCurrencyFull },
  ];
  const convSeries = [
    { data: rollingRecv, label: 'Received conv. rate (7d)', color: '#1976d2', showMark: false, connectNulls: false, valueFormatter: (v) => (v == null ? '' : `${v}%`) },
    { data: rollingSent, label: 'Sent conv. rate (7d)', color: '#90caf9', showMark: false, connectNulls: false, valueFormatter: (v) => (v == null ? '' : `${v}%`) },
    ...(flatCohort.length > 0
      ? [{ data: flatCohort, label: 'Partners avg', color: '#ff9800', showMark: false, connectNulls: true, curve: 'linear', valueFormatter: (v) => (v == null ? '' : `${v}%`) }]
      : []),
  ];

  const activeBarSeries =
    chartView === 'revenue' ? revenueSeries : chartView === 'orders' ? ordersSeries : spendSeries;
  const isCurrencyView = chartView !== 'orders';
  const allBarValues = activeBarSeries.flatMap((s) => s.data);
  const barDomain =
    chartView === 'revenue'
      ? niceDomain(allBarValues) // revenue includes negative net cost — allow below zero
      : { min: 0, max: niceDomain(allBarValues.map((v) => Math.max(0, v || 0))).max };

  const convValues = convSeries.flatMap((s) => s.data);
  const convDomain = { min: 0, max: niceDomain(convValues.map((v) => Math.max(0, v || 0))).max };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Trends</Typography>
        <Stack direction="row" spacing={0.5}>
          {PRESETS.map((p) => (
            <Chip
              key={p.label}
              label={p.label}
              size="small"
              variant={preset === p.label ? 'filled' : 'outlined'}
              color={preset === p.label ? 'primary' : 'default'}
              onClick={() => setPreset(p.label)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Stack>
      </Box>

      <ToggleButtonGroup
        value={chartView}
        exclusive
        onChange={(_, v) => v && setChartView(v)}
        size="small"
        fullWidth
        sx={{ mb: 1.5 }}
      >
        <ToggleButton value="revenue">Revenue & Cost</ToggleButton>
        <ToggleButton value="orders">Orders</ToggleButton>
        <ToggleButton value="spend">Spend & Earnings</ToggleButton>
        <ToggleButton value="conversion">Conversion Rate</ToggleButton>
      </ToggleButtonGroup>

      {chartView === 'conversion' ? (
        convLoading ? (
          <Skeleton variant="rectangular" height={CHART_HEIGHT} sx={{ borderRadius: 2 }} />
        ) : conversionTrends.length === 0 ? (
          <Typography color="textSecondary">No conversion data available for this period.</Typography>
        ) : (
          <Box>
            <FitChart
              ChartComponent={LineChart}
              xAxis={{ data: convXLabels, scaleType: 'band' }}
              series={convSeries}
              yValueFormatter={(v) => (v != null ? `${v}%` : '')}
              yDomain={convDomain}
            />
            <Legend series={convSeries} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Rolling 7-day conversion rate. Gaps indicate fewer than 5 clicks in the window.
            </Typography>
          </Box>
        )
      ) : trends.length === 0 ? (
        <Typography color="textSecondary">No trend data available for this period.</Typography>
      ) : (
        <>
          <FitChart
            ChartComponent={BarChart}
            xAxis={barXAxis}
            series={activeBarSeries}
            yValueFormatter={isCurrencyView ? formatCurrencyCompact : undefined}
            yDomain={barDomain}
          />
          <Legend series={activeBarSeries} />
        </>
      )}
    </Box>
  );
};

export default TrendCharts;
